const EMBED_HOST = /youtube|youtu\.be|vimeo|player\.vimeo/i

const isJsdom =
    typeof navigator !== 'undefined' && /jsdom/i.test(navigator.userAgent || '')

const INLINE_ALLOW =
    'autoplay; encrypted-media; picture-in-picture; playsinline'

const withEmbedInlineParams = (rawSrc: string): string => {
    try {
        const url = new URL(rawSrc, 'https://example.invalid')
        if (!EMBED_HOST.test(url.hostname + url.pathname + rawSrc)) {
            return rawSrc
        }
        const autoplay = url.searchParams.get('autoplay')
        if (autoplay !== '0' && autoplay !== 'false') {
            url.searchParams.set('autoplay', '1')
            url.searchParams.set('mute', '1')
            url.searchParams.set('muted', '1')
        }
        url.searchParams.set('playsinline', '1')
        if (url.origin === 'https://example.invalid') {
            return `${url.pathname}${url.search}${url.hash}`
        }
        return `${url.origin}${url.pathname}${url.search}${url.hash}`
    } catch {
        return rawSrc
    }
}

const tryPlayVideo = (video: HTMLVideoElement): void => {
    try {
        const playPromise = video.play()
        if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(() => undefined)
        }
    } catch {
        // autoplay bloqueado por el navegador
    }
}

const enableInlineOnVideo = (video: HTMLVideoElement): void => {
    video.setAttribute('playsinline', 'true')
    video.setAttribute('webkit-playsinline', 'true')
    video.playsInline = true

    const hasAutoplay =
        video.autoplay ||
        video.hasAttribute('autoplay') ||
        Boolean(video.closest('.jarallax'))
    if (hasAutoplay) {
        video.muted = true
        video.defaultMuted = true
        video.setAttribute('muted', '')
        video.setAttribute('autoplay', '')
        if (video.getAttribute('preload') === 'metadata') {
            video.setAttribute('preload', 'auto')
        }
    }

    if (isJsdom || !hasAutoplay) return

    const alreadyPrepared = video.dataset.inlinePlaybackApplied === '1'
    video.dataset.inlinePlaybackApplied = '1'

    if (video.readyState >= 2) {
        tryPlayVideo(video)
        return
    }

    video.addEventListener('canplay', () => tryPlayVideo(video), { once: true })
    video.addEventListener('loadeddata', () => tryPlayVideo(video), {
        once: true
    })

    if (!alreadyPrepared && typeof video.load === 'function') {
        try {
            video.load()
        } catch {
            tryPlayVideo(video)
        }
    } else {
        tryPlayVideo(video)
    }
}

const enableInlineOnIframe = (iframe: HTMLIFrameElement): void => {
    const src = iframe.getAttribute('src') || ''
    if (!EMBED_HOST.test(src)) return

    const next = withEmbedInlineParams(src)
    if (src !== next) {
        iframe.setAttribute('src', next)
    }

    iframe.removeAttribute('allowfullscreen')
    iframe.setAttribute('allow', INLINE_ALLOW)
}

/**
 * Reescribe el HTML CMS antes del primer paint: iOS necesita playsinline
 * en el markup inicial; mutar el iframe después aborta el autoplay en Android.
 */
export const prepareCmsMediaHtml = (html?: string | null): string => {
    if (!html) return html ?? ''

    const withVideos = html.replace(/<video\b([^>]*)>/gi, (_match, attrs) => {
        let nextAttrs = String(attrs)
        if (!/\bplaysinline\b/i.test(nextAttrs)) {
            nextAttrs += ' playsinline="true" webkit-playsinline="true"'
        }
        if (/\bautoplay\b/i.test(nextAttrs) && !/\bmuted\b/i.test(nextAttrs)) {
            nextAttrs += ' muted'
        }
        return `<video${nextAttrs}>`
    })

    return withVideos.replace(
        /(<iframe\b[^>]*\bsrc=["'])([^"']+)(["'][^>]*>)/gi,
        (match, prefix, src, suffix) => {
            if (!EMBED_HOST.test(src)) return match
            const nextSrc = withEmbedInlineParams(src)
            const withoutFs = `${prefix}${nextSrc}${suffix}`.replace(
                /\sallowfullscreen(="[^"]*")?/gi,
                ''
            )
            if (/\sallow=/i.test(withoutFs)) {
                return withoutFs.replace(
                    /\sallow=["'][^"']*["']/i,
                    ` allow="${INLINE_ALLOW}"`
                )
            }
            return withoutFs.replace(/>$/, ` allow="${INLINE_ALLOW}">`)
        }
    )
}

/**
 * iOS abre fullscreen si falta playsinline.
 * Android a veces no arranca el autoplay hasta recargar (SPA): reintenta play().
 */
export const applyInlineMobileVideoPlayback = (
    root: ParentNode | null | undefined
): void => {
    if (!root || typeof root.querySelectorAll !== 'function') return

    root.querySelectorAll('video').forEach((node) => {
        enableInlineOnVideo(node)
    })
    root.querySelectorAll('iframe').forEach((node) => {
        enableInlineOnIframe(node)
    })
}
